import {
  Checkbox,
  DismissibleTag,
  FilterableMultiSelect,
  InlineNotification,
  Pagination,
  Search,
  Select,
  SelectItem,
} from "@carbon/react";
import i18next from "../../../i18n";
import { Loader } from "../../../components";
import { BIDDING_TYPES } from "../../../utils";
import { MaterialType, TagResponse } from "../../../types";
import MaterialsCheckboxList from "../MaterialsCheckboxList/MaterialsCheckboxList";

type CompanyOption = { id: string; name: string };

type StepMaterialsProps = {
  materialType: MaterialType;
  onMaterialTypeChange: (type: MaterialType) => void;
  availableTags?: TagResponse[];
  selectedTags: TagResponse[];
  onTagsChange: (tags: TagResponse[]) => void;
  companyList?: CompanyOption[];
  selectedCompanyIds: string[];
  toggleCompany: (id: string) => void;
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  errorMessage: string | null;
  selectedComplexType: any;
  ordersToSubmit: any[];
  isLoading: boolean;
  allMaterials: any;
  materials: any;
  foundMaterials: any;
  materialsForBiddingPage: number;
  setMaterialsForBiddingPage: (page: number) => void;
  searchMaterialsPage: number;
  setSearchMaterialsPage: (page: number) => void;
  pageSize: number;
  onPageSizeChange: (size: number) => void;
  handleCheckboxChange: (material: any) => void;
};

/** The materials already chosen — shown as removable Carbon chips, separate from
 * the candidate table so the table only ever lists what is still selectable. */
const SelectedMaterials = ({
  ordersToSubmit,
  onRemove,
}: {
  ordersToSubmit: any[];
  onRemove: (material: any) => void;
}) => {
  if (!ordersToSubmit?.length) return null;
  return (
    <div className="order-selected">
      <p className="order-selected__label">
        {i18next.t("tenders.selectedMaterials")} ({ordersToSubmit.length})
      </p>
      <div className="order-selected__chips">
        {ordersToSubmit.map((material: any) => (
          <DismissibleTag
            key={material.id}
            type="blue"
            size="md"
            text={material.globalMaterialName || material.ean}
            title={`${material.ean} - ${material.globalMaterialName}`}
            onClose={() => onRemove(material)}
          />
        ))}
      </div>
    </div>
  );
};

/** A Carbon `Pagination` bar wired to the flow's 0-indexed page state and a real items-per-page
 * control — both the page and the size are threaded through to the material fetch. Replaces the
 * pre-Carbon Tailwind `UI/Pagination`. */
const MaterialsPager = ({
  currentPage,
  setPageNumber,
  total,
  pageSize,
  onPageSizeChange,
}: {
  currentPage: number;
  setPageNumber: (page: number) => void;
  total: number;
  pageSize: number;
  onPageSizeChange: (size: number) => void;
}) => (
  <Pagination
    className="order-pagination"
    page={currentPage + 1}
    pageSize={pageSize}
    pageSizes={[10, 20, 50]}
    totalItems={total}
    size="md"
    itemsPerPageText={i18next.t("pagination.itemsPerPage")}
    itemRangeText={(min: number, max: number, totalItems: number) =>
      `${min}–${max} ${i18next.t("pagination.of")} ${totalItems}`
    }
    pageRangeText={(_current: number, totalPages: number) =>
      `${i18next.t("pagination.of")} ${totalPages}`
    }
    onChange={({ page, pageSize: newSize }: { page: number; pageSize: number }) =>
      newSize !== pageSize ? onPageSizeChange(newSize) : setPageNumber(page - 1)
    }
  />
);

/** Step 2: material-type/tag/company/search filters, the chosen-materials chips,
 * and the candidate table. */
const StepMaterials = ({
  materialType,
  onMaterialTypeChange,
  availableTags,
  selectedTags,
  onTagsChange,
  companyList,
  selectedCompanyIds,
  toggleCompany,
  searchTerm,
  setSearchTerm,
  errorMessage,
  selectedComplexType,
  ordersToSubmit,
  isLoading,
  allMaterials,
  materials,
  foundMaterials,
  materialsForBiddingPage,
  setMaterialsForBiddingPage,
  searchMaterialsPage,
  setSearchMaterialsPage,
  pageSize,
  onPageSizeChange,
  handleCheckboxChange,
}: StepMaterialsProps) => {
  const showJointWarning =
    selectedComplexType?.value === BIDDING_TYPES.JOINT &&
    ordersToSubmit.length < 2;

  return (
    <>
      <div className="order-filters">
        <Select
          id="order-material-type"
          className="order-filters__type"
          labelText={i18next.t("materials.type")}
          value={materialType}
          onChange={(e) => onMaterialTypeChange(e.target.value as MaterialType)}
        >
          <SelectItem value={MaterialType.ALL} text={i18next.t("materialTypes.all")} />
          <SelectItem
            value={MaterialType.DIRECT_GOODS}
            text={i18next.t("materialTypes.pi")}
          />
          <SelectItem
            value={MaterialType.INDIRECT_GOODS}
            text={i18next.t("materialTypes.npi")}
          />
          <SelectItem
            value={MaterialType.SERVICES}
            text={i18next.t("materialTypes.services")}
          />
        </Select>

        {availableTags && availableTags.length > 0 && (
          <FilterableMultiSelect
            id="order-material-tags"
            className="order-filters__tags"
            titleText={i18next.t("materials.filterByTags")}
            placeholder={i18next.t("materials.filterByTags")}
            items={availableTags}
            itemToString={(tag) => tag?.name ?? ""}
            selectedItems={selectedTags}
            onChange={({ selectedItems }) => onTagsChange(selectedItems ?? [])}
          />
        )}

        <div className="order-filters__search">
          <Search
            id="order-material-search"
            labelText={i18next.t("cta.search")}
            placeholder={i18next.t("cta.search")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {companyList && companyList.length > 1 && (
        <div className="order-companies">
          <span className="order-companies__label">
            {i18next.t("purchaseRequests.filters.companies")}
          </span>
          <div className="order-companies__list">
            {companyList.map((c) => (
              <Checkbox
                key={c.id}
                id={`order-company-${c.id}`}
                labelText={c.name}
                checked={selectedCompanyIds.includes(c.id)}
                onChange={() => toggleCompany(c.id)}
              />
            ))}
          </div>
        </div>
      )}

      {errorMessage && (
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          title={errorMessage}
        />
      )}

      {showJointWarning && (
        <InlineNotification
          kind="warning"
          lowContrast
          hideCloseButton
          title={i18next.t("tenders.selectMoreThanOne")}
        />
      )}

      <SelectedMaterials
        ordersToSubmit={ordersToSubmit}
        onRemove={handleCheckboxChange}
      />

      {isLoading ? (
        <div className="order-loader">
          <Loader />
        </div>
      ) : (
        <>
          {materialType === MaterialType.ALL &&
            allMaterials?.content?.length > 0 &&
            searchTerm === "" && (
              <>
                <MaterialsCheckboxList
                  isChecked={ordersToSubmit}
                  materials={allMaterials}
                  handleCheckboxChange={handleCheckboxChange}
                />
                <MaterialsPager
                  currentPage={materialsForBiddingPage}
                  setPageNumber={setMaterialsForBiddingPage}
                  total={allMaterials.totalElements}
                  pageSize={pageSize}
                  onPageSizeChange={onPageSizeChange}
                />
              </>
            )}
          {materialType !== MaterialType.ALL &&
            materials?.content?.length > 0 &&
            searchTerm === "" && (
              <>
                <MaterialsCheckboxList
                  isChecked={ordersToSubmit}
                  materials={materials}
                  handleCheckboxChange={handleCheckboxChange}
                />
                <MaterialsPager
                  currentPage={materialsForBiddingPage}
                  setPageNumber={setMaterialsForBiddingPage}
                  total={materials.totalElements}
                  pageSize={pageSize}
                  onPageSizeChange={onPageSizeChange}
                />
              </>
            )}
          {foundMaterials?.content && searchTerm !== "" && (
            <>
              <MaterialsCheckboxList
                searchTerm={searchTerm}
                isChecked={ordersToSubmit}
                foundMaterials={foundMaterials}
                handleCheckboxChange={handleCheckboxChange}
              />
              <MaterialsPager
                currentPage={searchMaterialsPage}
                setPageNumber={setSearchMaterialsPage}
                total={foundMaterials.totalElements}
                pageSize={pageSize}
                onPageSizeChange={onPageSizeChange}
              />
            </>
          )}
        </>
      )}
    </>
  );
};

export default StepMaterials;
